import { CalendarEvent, WeekInfo, CalendarDay, DayOfWeek, AppError } from '../types';

/**
 * Gestor de la interfaz de usuario
 */
export class UIManager {
  private readonly HOURS_START = 6;
  private readonly HOURS_END = 23;

  constructor() {
    // Inicializar configuración por defecto
  }

  /**
   * Muestra la pantalla de login
   */
  showLoginScreen(): void {
    const loginScreen = document.getElementById('login-screen');
    const calendarScreen = document.getElementById('calendar-screen');
    
    if (loginScreen) loginScreen.classList.remove('hidden');
    if (calendarScreen) calendarScreen.classList.add('hidden');
  }

  /**
   * Muestra la pantalla del calendario
   */
  showCalendarScreen(): void {
    const loginScreen = document.getElementById('login-screen');
    const calendarScreen = document.getElementById('calendar-screen');
    
    if (loginScreen) loginScreen.classList.add('hidden');
    if (calendarScreen) calendarScreen.classList.remove('hidden');
  }

  /**
   * Establece el estado de carga del botón de login
   */
  setLoginLoading(isLoading: boolean): void {
    const loginButton = document.getElementById('login-button') as HTMLButtonElement;
    const buttonText = loginButton?.querySelector('.button-text');
    const buttonLoader = loginButton?.querySelector('.button-loader');

    if (loginButton) {
      loginButton.disabled = isLoading;
      
      if (buttonText) {
        buttonText.classList.toggle('hidden', isLoading);
      }
      
      if (buttonLoader) {
        buttonLoader.classList.toggle('hidden', !isLoading);
      }
    }
  }

  /**
   * Inicializa el grid del calendario con las horas y estructura básica
   */
  initializeCalendarGrid(): void {
    this.generateTimeColumn();
    this.generateDayColumns();
    this.updateDayHeaders();
  }

  /**
   * Genera la columna de horas
   */
  private generateTimeColumn(): void {
    const timeColumn = document.querySelector('.time-column');
    if (!timeColumn) return;

    timeColumn.innerHTML = '';

    for (let hour = this.HOURS_START; hour <= this.HOURS_END; hour++) {
      const timeSlot = document.createElement('div');
      timeSlot.className = 'time-slot';
      
      const timeText = document.createElement('span');
      timeText.className = 'time-text';
      timeText.textContent = this.formatHour(hour);
      
      timeSlot.appendChild(timeText);
      timeColumn.appendChild(timeSlot);
    }
  }

  /**
   * Genera las líneas de hora para cada columna de día
   */
  private generateDayColumns(): void {
    const dayColumns = document.querySelectorAll('.day-column');
    
    dayColumns.forEach(column => {
      column.innerHTML = '';
      
      for (let hour = this.HOURS_START; hour <= this.HOURS_END; hour++) {
        const hourLine = document.createElement('div');
        hourLine.className = 'hour-line';
        hourLine.dataset.hour = hour.toString();
        column.appendChild(hourLine);
      }
    });
  }

  /**
   * Actualiza los encabezados de los días de la semana
   */
  private updateDayHeaders(): void {
    const currentDate = new Date();
    const startOfWeek = this.getStartOfWeek(currentDate);
    
    const dayHeaders = document.querySelectorAll('.day-header');
    const dayNames = ['LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB', 'DOM'];
    
    dayHeaders.forEach((header, index) => {
      const dayDate = new Date(startOfWeek);
      dayDate.setDate(dayDate.getDate() + index);
      
      const dayNameElement = header.querySelector('.day-name');
      const dayNumberElement = header.querySelector('.day-number');
      
      if (dayNameElement && index < dayNames.length) {
        dayNameElement.textContent = dayNames[index] || '';
      }
      
      if (dayNumberElement) {
        dayNumberElement.textContent = dayDate.getDate().toString();
        
        // Marcar el día actual
        const isToday = this.isSameDay(dayDate, currentDate);
        dayNumberElement.classList.toggle('today', isToday);
      }
    });
  }

  /**
   * Actualiza la información de la semana en el header
   */
  updateWeekInfo(weekInfo: WeekInfo): void {
    const currentWeekElement = document.getElementById('current-week');
    if (currentWeekElement) {
      // Generar el texto de visualización de la semana
      const displayText = this.generateWeekDisplayText(weekInfo.start, weekInfo.end);
      currentWeekElement.textContent = displayText;
    }

    // Actualizar los números de los días en los headers
    this.updateDayHeadersForWeek(weekInfo.start);
  }

  /**
   * Genera el texto de visualización para una semana
   */
  private generateWeekDisplayText(startDate: Date, endDate: Date): string {
    const startMonth = startDate.toLocaleDateString('es-ES', { month: 'short' });
    const endMonth = endDate.toLocaleDateString('es-ES', { month: 'short' });
    const startDay = startDate.getDate();
    const endDay = endDate.getDate();
    const year = startDate.getFullYear();

    if (startMonth === endMonth) {
      return `${startDay} - ${endDay} ${startMonth} ${year}`;
    } else {
      return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${year}`;
    }
  }

  /**
   * Actualiza los encabezados de días para una semana específica
   */
  private updateDayHeadersForWeek(startDate: Date): void {
    const dayHeaders = document.querySelectorAll('.day-header');
    const dayNames = ['LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB', 'DOM'];
    const today = new Date();
    
    dayHeaders.forEach((header, index) => {
      // No hay time column header en .day-header, así que index va de 0-6 para los 7 días
      const dayDate = new Date(startDate);
      dayDate.setDate(dayDate.getDate() + index);
      
      const dayNameElement = header.querySelector('.day-name');
      const dayNumberElement = header.querySelector('.day-number');
      
      // Actualizar nombre del día
      if (dayNameElement && index < dayNames.length) {
        dayNameElement.textContent = dayNames[index] || '';
      }
      
      // Actualizar número del día
      if (dayNumberElement) {
        dayNumberElement.textContent = dayDate.getDate().toString();
        
        // Marcar el día actual
        const isToday = this.isSameDay(dayDate, today);
        dayNumberElement.classList.toggle('today', isToday);
      }
    });
  }

  /**
   * Muestra los eventos en el calendario
   */
  displayEvents(events: CalendarEvent[]): void {
    // Limpiar eventos existentes
    this.clearExistingEvents();
    
    // Ocultar estados de carga y vacío
    this.hideLoadingState();
    this.hideEmptyState();
    
    // Agrupar eventos por día
    const eventsByDay = this.groupEventsByDay(events);
    
    // Renderizar eventos para cada día
    Object.entries(eventsByDay).forEach(([dayKey, dayEvents]) => {
      this.renderDayEvents(dayKey, dayEvents);
    });
  }

  /**
   * Agrupa los eventos por día de la semana
   */
  private groupEventsByDay(events: CalendarEvent[]): Record<string, CalendarEvent[]> {
    const eventsByDay: Record<string, CalendarEvent[]> = {
      'monday': [],
      'tuesday': [],
      'wednesday': [],
      'thursday': [],
      'friday': [],
      'saturday': [],
      'sunday': []
    };

    events.forEach(event => {
      const dayKey = this.getDayKey(event.startTime);
      if (eventsByDay[dayKey]) {
        eventsByDay[dayKey].push(event);
      }
    });

    return eventsByDay;
  }

  /**
   * Obtiene la clave del día para un evento
   */
  private getDayKey(date: Date): string {
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return dayNames[date.getDay()] || 'sunday';
  }

  /**
   * Renderiza los eventos de un día específico
   */
  private renderDayEvents(dayKey: string, events: CalendarEvent[]): void {
    const dayColumn = document.querySelector(`.day-column[data-day="${dayKey}"]`);
    if (!dayColumn) return;

    events.forEach(event => {
      const eventElement = this.createEventElement(event);
      this.positionEventElement(eventElement, event);
      dayColumn.appendChild(eventElement);
    });
  }

  /**
   * Crea un elemento DOM para un evento
   */
  private createEventElement(event: CalendarEvent): HTMLElement {
    const eventElement = document.createElement('div');
    eventElement.className = 'calendar-event';
    eventElement.dataset.eventId = event.id;
    
    // Aplicar clase de estado
    eventElement.classList.add(`event-${event.status}`);
    
    // Aplicar color del calendario si existe
    if (event.calendarColor) {
      eventElement.style.backgroundColor = event.calendarColor;
      eventElement.style.borderLeft = `4px solid ${event.calendarColor}`;
    } else if (event.color) {
      eventElement.style.backgroundColor = event.color;
    }

    // Crear contenido del evento
    const titleElement = document.createElement('div');
    titleElement.className = 'event-title';
    titleElement.textContent = event.title;

    const timeElement = document.createElement('div');
    timeElement.className = 'event-time';
    timeElement.textContent = this.formatEventTime(event);

    // Agregar información del calendario
    const calendarElement = document.createElement('div');
    calendarElement.className = 'event-calendar';
    calendarElement.textContent = event.calendarName || 'Calendario';

    eventElement.appendChild(titleElement);
    if (!event.isAllDay) {
      eventElement.appendChild(timeElement);
    }
    eventElement.appendChild(calendarElement);

    // Agregar event listener para mostrar detalles
    eventElement.addEventListener('click', () => {
      this.showEventModal(event);
    });

    return eventElement;
  }

  /**
   * Posiciona un elemento de evento en el grid
   */
  private positionEventElement(element: HTMLElement, event: CalendarEvent): void {
    const startHour = event.startTime.getHours();
    const startMinute = event.startTime.getMinutes();
    const endHour = event.endTime.getHours();
    const endMinute = event.endTime.getMinutes();

    // Calcular posición top (desde HOURS_START)
    const topOffset = Math.max(0, startHour - this.HOURS_START) * 60 + startMinute;
    
    // Calcular duración en minutos
    const duration = (endHour * 60 + endMinute) - (startHour * 60 + startMinute);
    const height = Math.max(20, duration); // Altura mínima de 20px

    // Aplicar posicionamiento
    element.style.position = 'absolute';
    element.style.top = `${topOffset}px`;
    element.style.height = `${height}px`;
    element.style.zIndex = '10';
  }

  /**
   * Muestra el modal con detalles del evento
   */
  showEventModal(event: CalendarEvent): void {
    const modal = document.getElementById('event-modal');
    const title = document.getElementById('event-title');
    const time = document.getElementById('event-time');
    const location = document.getElementById('event-location');
    const locationContainer = document.getElementById('event-location-container');
    const description = document.getElementById('event-description');
    const descriptionContainer = document.getElementById('event-description-container');

    if (!modal) return;

    // Rellenar información del evento
    if (title) title.textContent = event.title;
    if (time) time.textContent = this.formatEventTime(event);

    // Mostrar u ocultar ubicación
    if (location && locationContainer) {
      if (event.location) {
        location.textContent = event.location;
        locationContainer.style.display = 'flex';
      } else {
        locationContainer.style.display = 'none';
      }
    }

    // Mostrar u ocultar descripción
    if (description && descriptionContainer) {
      if (event.description) {
        description.textContent = event.description;
        descriptionContainer.style.display = 'flex';
      } else {
        descriptionContainer.style.display = 'none';
      }
    }

    modal.classList.remove('hidden');
  }

  /**
   * Oculta el modal de detalles del evento
   */
  hideEventModal(): void {
    const modal = document.getElementById('event-modal');
    if (modal) {
      modal.classList.add('hidden');
    }
  }

  /**
   * Muestra el estado de carga
   */
  showLoadingState(): void {
    const loadingState = document.getElementById('loading-state');
    const emptyState = document.getElementById('empty-state');
    const calendarContainer = document.querySelector('.calendar-container') as HTMLElement;

    if (loadingState) loadingState.classList.remove('hidden');
    if (emptyState) emptyState.classList.add('hidden');
    if (calendarContainer) calendarContainer.style.display = 'none';
  }

  /**
   * Oculta el estado de carga
   */
  hideLoadingState(): void {
    const loadingState = document.getElementById('loading-state');
    const calendarContainer = document.querySelector('.calendar-container') as HTMLElement;

    if (loadingState) loadingState.classList.add('hidden');
    if (calendarContainer) calendarContainer.style.display = 'block';
  }

  /**
   * Muestra el estado vacío
   */
  showEmptyState(): void {
    const emptyState = document.getElementById('empty-state');
    const loadingState = document.getElementById('loading-state');
    const calendarContainer = document.querySelector('.calendar-container') as HTMLElement;

    if (emptyState) emptyState.classList.remove('hidden');
    if (loadingState) loadingState.classList.add('hidden');
    if (calendarContainer) calendarContainer.style.display = 'none';
  }

  /**
   * Oculta el estado vacío
   */
  hideEmptyState(): void {
    const emptyState = document.getElementById('empty-state');
    const calendarContainer = document.querySelector('.calendar-container') as HTMLElement;

    if (emptyState) emptyState.classList.add('hidden');
    if (calendarContainer) calendarContainer.style.display = 'block';
  }

  /**
   * Limpia los eventos existentes del calendario
   */
  private clearExistingEvents(): void {
    const existingEvents = document.querySelectorAll('.calendar-event');
    existingEvents.forEach(event => event.remove());
  }

  /**
   * Actualiza la información del usuario en el header
   */
  updateUserInfo(currentUser: string, ownerEmail: string): void {
    const currentUserElement = document.getElementById('current-user');
    const ownerNameElement = document.getElementById('owner-name');
    
    if (currentUserElement) {
      currentUserElement.textContent = currentUser;
    }
    
    if (ownerNameElement) {
      // Extraer el nombre del email del propietario para mostrarlo
      const ownerName = ownerEmail.split('@')[0] ?? 'Propietario';
      ownerNameElement.textContent = ownerName;
    }
  }

  /**
   * Muestra un mensaje de error
   */
  showError(message: string): void {
    // Implementar sistema de notificaciones toast
    console.error('Error:', message);
    alert(message); // Temporal - reemplazar por toast notification
  }

  /**
   * Muestra una advertencia sobre calendarios no compartidos
   */
  showCalendarSharingWarning(currentUserEmail: string): void {
    const warningMessage = `
⚠️ NO SE ENCONTRARON CALENDARIOS DEL PROPIETARIO

USUARIO ACTUAL: ${currentUserEmail}

POSIBLES CAUSAS:
• Los calendarios no han sido compartidos contigo
• No tienes permisos para ver los calendarios
• Error de configuración

SOLUCIÓN:
1. El propietario (luis.rioja11@gmail.com) debe:
   - Ir a Google Calendar
   - Compartir cada calendario contigo
   - Darte permisos de "Ver todos los detalles"

2. Tú debes:
   - Revisar emails de invitación de Google
   - Aceptar las invitaciones de calendario

CONTACTA AL PROPIETARIO PARA COMPARTIR LOS CALENDARIOS.
    `.trim();

    console.warn('⚠️ Advertencia de calendarios:', warningMessage);
    alert(warningMessage);
  }

  /**
   * Muestra estado cuando no hay calendarios disponibles
   */
  showNoCalendarsState(currentUserEmail: string): void {
    const calendarGrid = document.querySelector('.calendar-grid');
    
    if (calendarGrid) {
      calendarGrid.innerHTML = `
        <div class="no-calendars-warning" style="
          grid-column: 1 / -1;
          text-align: center;
          padding: 2rem;
          background: #fff3cd;
          border: 1px solid #ffeaa7;
          border-radius: 8px;
          margin: 1rem;
        ">
          <div style="font-size: 1.5rem; margin-bottom: 1rem;">
            ⚠️ No se encontraron calendarios del propietario
          </div>
          <div style="margin-bottom: 1rem; color: #856404;">
            <strong>Usuario actual:</strong> ${currentUserEmail}
          </div>
          <div style="text-align: left; max-width: 600px; margin: 0 auto;">
            <h4>Posibles causas:</h4>
            <ul>
              <li>Los calendarios no han sido compartidos contigo</li>
              <li>No tienes permisos para ver los calendarios</li>
              <li>Error de configuración en Google Calendar</li>
            </ul>
            
            <h4>Solución:</h4>
            <ol>
              <li><strong>El propietario (luis.rioja11@gmail.com) debe:</strong>
                <ul>
                  <li>Ir a Google Calendar</li>
                  <li>Compartir cada calendario contigo</li>
                  <li>Darte permisos de "Ver todos los detalles"</li>
                </ul>
              </li>
              <li><strong>Tú debes:</strong>
                <ul>
                  <li>Revisar emails de invitación de Google</li>
                  <li>Aceptar las invitaciones de calendario</li>
                </ul>
              </li>
            </ol>
          </div>
          <div style="margin-top: 1rem; padding: 1rem; background: #e3f2fd; border-radius: 4px;">
            💡 <strong>Tip:</strong> Contacta al propietario para que comparta los calendarios contigo.
          </div>
        </div>
      `;
    }
  }

  /**
   * Formatea una hora para mostrar
   */
  private formatHour(hour: number): string {
    return `${hour.toString().padStart(2, '0')}:00`;
  }

  /**
   * Formatea el tiempo de un evento
   */
  private formatEventTime(event: CalendarEvent): string {
    if (event.isAllDay) {
      return 'Todo el día';
    }

    const startTime = event.startTime.toLocaleTimeString('es-ES', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
    const endTime = event.endTime.toLocaleTimeString('es-ES', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });

    return `${startTime} - ${endTime}`;
  }

  /**
   * Obtiene el inicio de la semana (lunes)
   */
  private getStartOfWeek(date: Date): Date {
    const start = new Date(date);
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1);
    start.setDate(diff);
    start.setHours(0, 0, 0, 0);
    return start;
  }

  /**
   * Verifica si dos fechas son el mismo día
   */
  private isSameDay(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  }

  // Callbacks para manejo de errores y diagnósticos
  onRetryAuth?: () => void;
  onDiagnoseAuth?: () => void;
  onTroubleshootAuth?: () => void;
  onResetAuth?: () => void;

  /**
   * Muestra la pantalla de error de autenticación
   */
  showAuthError(error: AppError): void {
    const content = document.querySelector('.content');
    if (!content) return;

    // Determinar el mensaje específico del error
    let errorTitle = 'Error de Autenticación';
    let errorMessage = error.message;
    let actionButton = '';
    let diagnosticInfo = '';

    switch (error.code) {
      case 'USER_NOT_AUTHORIZED':
        errorTitle = 'Acceso No Autorizado';
        errorMessage = 'Tu cuenta no está en la lista de usuarios autorizados para ver este calendario.';
        break;

      case 'ACCESS_DENIED':
        errorTitle = 'Aplicación No Verificada por Google';
        errorMessage = 'La aplicación está en modo de desarrollo y requiere configuración adicional en Google Cloud Console.';
        diagnosticInfo = `
          <div class="diagnostic-info">
            <h3>🔐 Configuración requerida en Google Cloud Console:</h3>
            <ul>
              <li>📝 <strong>Paso 1:</strong> Ve a <a href="https://console.cloud.google.com" target="_blank">console.cloud.google.com</a></li>
              <li>👤 <strong>Paso 2:</strong> En "OAuth consent screen" → "Test users", agrega: <code>luis.rioja11@gmail.com</code></li>
              <li>🌐 <strong>Paso 3:</strong> En "Credentials", verifica que <code>http://localhost:3000</code> esté en "Authorized JavaScript origins"</li>
              <li>⏳ <strong>Paso 4:</strong> Espera 5-10 minutos para que los cambios se propaguen</li>
              <li>🔄 <strong>Paso 5:</strong> Recarga esta página e intenta de nuevo</li>
            </ul>
            <p><strong>💡 Alternativa:</strong> En la pantalla de Google, haz clic en "Avanzado" → "Ir a CalendarChecker (no seguro)"</p>
          </div>
        `;
        actionButton = `
          <button id="open-google-console" class="diagnose-button">🌐 Abrir Google Console</button>
          <button id="retry-auth" class="retry-button">🔄 Reintentar después de configurar</button>
        `;
        break;
        
      case 'GOOGLE_AUTH_ERROR':
        errorTitle = 'Error de Google OAuth';
        if (error.message.includes('idpiframe_initialization_failed')) {
          errorMessage = 'Error de inicialización de autenticación. Esto puede deberse a configuración de cookies o bloqueadores.';
          diagnosticInfo = `
            <div class="diagnostic-info">
              <h3>🔧 Soluciones posibles:</h3>
              <ul>
                <li>🍪 Habilita cookies de terceros en tu navegador</li>
                <li>🚫 Desactiva bloqueadores de anuncios temporalmente</li>
                <li>🔓 Prueba en modo incógnito</li>
                <li>🧹 Limpia la caché y cookies del navegador</li>
                <li>🌐 Verifica la configuración en Google Cloud Console</li>
              </ul>
            </div>
          `;
          actionButton = `
            <button id="diagnose-auth" class="diagnose-button">🔍 Diagnóstico Completo</button>
            <button id="troubleshoot-auth" class="troubleshoot-button">🔧 Auto-Reparar</button>
            <button id="retry-auth" class="retry-button">🔄 Reintentar</button>
          `;
        }
        break;

      case 'INVALID_ORIGIN':
        errorTitle = 'Origen No Válido';
        errorMessage = 'El dominio localhost no está configurado como origen autorizado.';
        diagnosticInfo = `
          <div class="diagnostic-info">
            <h3>🌐 Configuración de dominios en Google Cloud Console:</h3>
            <ul>
              <li>📍 Ve a "Credentials" → Tu OAuth 2.0 Client</li>
              <li>✅ En "Authorized JavaScript origins" debe estar: <code>http://localhost:3000</code></li>
              <li>✅ En "Authorized redirect URIs" debe estar: <code>http://localhost:3000</code></li>
            </ul>
          </div>
        `;
        actionButton = `
          <button id="open-google-console" class="diagnose-button">🌐 Abrir Google Console</button>
          <button id="retry-auth" class="retry-button">🔄 Reintentar</button>
        `;
        break;
        
      case 'API_LOAD_FAILED':
        errorTitle = 'Error de Conexión';
        errorMessage = 'No se pudo cargar la API de Google. Verifica tu conexión a internet.';
        actionButton = `
          <button id="diagnose-auth" class="diagnose-button">🔍 Diagnóstico</button>
          <button id="retry-auth" class="retry-button">🔄 Reintentar</button>
        `;
        break;
        
      default:
        errorMessage = error.message || 'Ha ocurrido un error inesperado durante la autenticación.';
        actionButton = `
          <button id="diagnose-auth" class="diagnose-button">🔍 Diagnóstico</button>
          <button id="retry-auth" class="retry-button">🔄 Reintentar</button>
        `;
    }

    content.innerHTML = `
      <div class="auth-error">
        <div class="error-icon">⚠️</div>
        <h2>${errorTitle}</h2>
        <p class="error-message">${errorMessage}</p>
        ${diagnosticInfo}
        <div class="error-actions">
          ${actionButton}
          <button id="back-to-login" class="secondary-button">🔙 Volver al Login</button>
          <button id="reset-auth" class="reset-button">🗑️ Reiniciar Sistema</button>
        </div>
      </div>
    `;

    // Event listeners para los botones
    this.setupErrorActionButtons();
  }

  /**
   * Configura los event listeners para los botones de acciones de error
   */
  private setupErrorActionButtons(): void {
    // Botón de diagnóstico
    const diagnoseButton = document.getElementById('diagnose-auth');
    if (diagnoseButton) {
      diagnoseButton.addEventListener('click', () => {
        if (this.onDiagnoseAuth) {
          this.onDiagnoseAuth();
        }
      });
    }

    // Botón de auto-reparación
    const troubleshootButton = document.getElementById('troubleshoot-auth');
    if (troubleshootButton) {
      troubleshootButton.addEventListener('click', () => {
        this.showLoading('Intentando resolver automáticamente...');
        if (this.onTroubleshootAuth) {
          this.onTroubleshootAuth();
        }
      });
    }

    // Botón de reintentar
    const retryButton = document.getElementById('retry-auth');
    if (retryButton) {
      retryButton.addEventListener('click', () => {
        this.showLoading('Reintentando autenticación...');
        if (this.onRetryAuth) {
          this.onRetryAuth();
        }
      });
    }

    // Botón de abrir Google Console
    const googleConsoleButton = document.getElementById('open-google-console');
    if (googleConsoleButton) {
      googleConsoleButton.addEventListener('click', () => {
        window.open('https://console.cloud.google.com/apis/credentials', '_blank');
      });
    }

    // Botón de volver al login
    const backButton = document.getElementById('back-to-login');
    if (backButton) {
      backButton.addEventListener('click', () => {
        this.showLoginScreen();
      });
    }

    // Botón de reiniciar sistema
    const resetButton = document.getElementById('reset-auth');
    if (resetButton) {
      resetButton.addEventListener('click', () => {
        if (confirm('¿Estás seguro de que quieres reiniciar completamente el sistema de autenticación? Esto recargará la página.')) {
          if (this.onResetAuth) {
            this.onResetAuth();
          }
        }
      });
    }
  }

  /**
   * Muestra pantalla de carga con mensaje personalizado
   */
  showLoading(message: string): void {
    const content = document.querySelector('.content');
    if (!content) return;

    content.innerHTML = `
      <div class="loading-screen">
        <div class="loading-spinner"></div>
        <p>${message}</p>
      </div>
    `;
  }
}
